/**
 * Axios API client setup and utility functions for frontend calls.
 * 
 * Key Components:
 * api: Axios instance configured with base URL and JWT authentication interceptor.
 * configureAuthToken(): Sets or clears the JWT token for requests.
 * fetchStats(), fetchDocuments(), fetchSegments(), fetchProcessedContent(): Database info endpoints.
 * generateExam(), checkGenerationStatus(), downloadExam(), fetchAvailableMarks(): Exam generation endpoints.
 * fetchTopicPaths(), fetchAvailableTopics(), [other topic/review functions]: Topic and review related endpoints.
 * fetchSubscriptionPlans(), fetchSubscriptionStatus(), createCheckoutSession(), cancelSubscription(): Subscription endpoints.
 * 
 * File Dependencies:
 * This file uses: axios
 * This file is used by: Various page and component files in the frontend.
 */

import axios from 'axios';

// Create an axios instance with default config
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8001',
});

// Add request interceptor to set Content-Type only when not FormData
api.interceptors.request.use(
  (config) => {
    // Only set Content-Type to JSON if it's not FormData
    if (!(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Variable to hold the current interceptor ID
let interceptorId = null;

// Function to set up auth error handler
let authErrorHandler = null;

// Configure auth error handling callback
export const configureAuthErrorHandler = (handler) => {
  authErrorHandler = handler;
};

// Function to configure JWT token interceptor
export const configureAuthToken = (token) => {
  // Eject the previous interceptor if it exists
  if (interceptorId !== null) {
    api.interceptors.request.eject(interceptorId);
    interceptorId = null; // Reset ID after ejecting
  }

  // Add a new interceptor only if a token is provided
  if (token) {
    interceptorId = api.interceptors.request.use(
      (config) => {
        // Use the provided JWT token
        config.headers['Authorization'] = `Bearer ${token}`;
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }
  // If no token is provided, we simply don't add the interceptor,
  // effectively clearing the token for future requests.
};

// Add response interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Call the auth error handler if it's set
      if (authErrorHandler) {
        authErrorHandler();
      }
    }
    return Promise.reject(error);
  }
);


// Authentication API functions
export const loginUser = (username, password) => {
  return api.post('/auth/login', { username, password });
};

export const registerUser = (username, email, firstName, lastName, password, role = 'STUDENT', referralToken = null) => {
  const data = {
    username, 
    email, 
    first_name: firstName,
    last_name: lastName,
    password, 
    role
  };
  
  // Include referral token if provided
  if (referralToken) {
    data.referral_token = referralToken;
  }
  
  return api.post('/auth/register', data);
};

export const logoutUser = () => {
  return api.post('/auth/logout');
};

export const fetchUserProfile = () => {
  return api.get('/auth/me');
};

export const completeFirstLogin = (token, newPassword) => {
  return api.post('/auth/first-login', { token, new_password: newPassword });
};

export const completeFirstLoginAuthenticated = (newPassword) => {
  return api.post('/auth/first-login/authenticated', { new_password: newPassword });
};

export const forgotPassword = (email) => {
  return api.post('/auth/forgot-password', { email });
};

export const resetPassword = (token, newPassword) => {
  return api.post('/auth/reset-password', { token, new_password: newPassword });
};

export const verifyEmail = (token) => {
  return api.post('/auth/verify-email', { token });
};

export const resendVerificationEmail = (email) => {
  return api.post('/auth/resend-verification', { email });
};

export const fetchTrialStatus = () => {
  return api.get('/auth/trial-status');
};

// Database API functions
export const fetchStats = () => {
  return api.get('/database/stats');
};

export const fetchDocuments = () => {
  return api.get('/database/documents');
};

export const fetchSegments = (documentId = null) => {
  const params = documentId ? { document_id: documentId } : {};
  return api.get('/database/segments', { params });
};

export const fetchProcessedContent = (segmentId) => {
  return api.get(`/database/segments/${segmentId}/processed`);
};

// Exam Generation API functions
export const generateExam = (params) => {
  return api.post('/generate/exam', params);
};

export const checkGenerationStatus = (taskId) => {
  return api.get(`/generate/exam/${taskId}`);
};

export const downloadExam = (taskId) => {
  return api.get(`/generate/exam/${taskId}/download`, {
    responseType: 'blob',
  });
};

// Fetch available marks based on selected topics and difficulty
export const fetchAvailableMarks = (topics, difficulty, courseId = null, topicIds = null) => {
  // Ensure topics is an array, even if empty
  const topicParams = Array.isArray(topics) ? topics : [];
  const params = {
    topics: topicParams,
    difficulty: difficulty
  };
  
  // Add topic_ids if provided
  if (topicIds && topicIds.length > 0) {
    params.topic_ids = topicIds;
  }
  
  // Add course_id if provided
  if (courseId) {
    params.course_id = courseId;
  }
  
  return api.get('/generate/available-marks', {
    params: params,
    // Ensure array parameters are serialized correctly (if needed by FastAPI)
    // Axios might handle this by default, but can be explicit:
    paramsSerializer: params => {
        const usp = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                value.forEach(val => usp.append(key, val));
            } else {
                usp.append(key, value);
            }
        });
        return usp.toString();
      }
  });
};

// Topic related API functions

// Fetch all valid topic paths (used in Review override dropdown)
export const fetchTopicPaths = () => {
  return api.get('/topics/paths');
};

// Fetch the nested topic tree, filtered to only those with questions
export const fetchAvailableTopics = (courseId = null) => {
  const params = courseId ? { course_id: courseId } : {};
  return api.get('/topics/with_questions', { params });
};

// Fetch available courses
export const fetchCourses = () => {
  return api.get('/courses');
};

// Course management API functions
export const createCourse = (name) => {
  return api.post('/courses', { name });
};

export const updateCourse = (courseId, name) => {
  return api.put(`/courses/${courseId}`, { name });
};

export const deleteCourse = (courseId) => {
  return api.delete(`/courses/${courseId}`);
};

// Topic management functions
export const fetchTopicsByCourse = () => {
  return api.get('/topics/by-course');
};

export const createTopic = (path, courseId) => {
  return api.post('/topics', { path, course_id: courseId });
};

export const createTopicsBulk = (paths, courseId) => {
  return api.post('/topics/bulk', { paths, course_id: courseId });
};

export const deleteTopic = (topicId) => {
  return api.delete('/topics', { data: { topic_id: topicId } });
};

export const moveTopic = (topicId, targetCourseId) => {
  return api.put(`/topics/${topicId}/move`, { 
    topic_id: topicId, 
    target_course_id: targetCourseId 
  });
};

export const copyTopic = (topicId, targetCourseId, includeSubtopics = true) => {
  return api.post(`/topics/${topicId}/copy`, { 
    topic_id: topicId, 
    target_course_id: targetCourseId,
    include_subtopics: includeSubtopics
  });
};

export const reorderTopics = (parentPath, orderedPaths, courseId) => {
  return api.put('/topics/reorder', {
    parent_path: parentPath,
    ordered_paths: orderedPaths,
    course_id: courseId
  });
};

export const fetchSuggestedTopics = (courseId) => {
  return api.get(`/topics/suggested/${courseId}`);
};

// Subscription API functions
export const fetchSubscriptionPlans = () => {
  return api.get('/subscriptions/plans');
};

export const fetchSubscriptionStatus = () => {
  return api.get('/subscriptions/status');
};

export const createCheckoutSession = (planId, successUrl, cancelUrl, couponCode = null) => {
  const payload = {
    plan_id: planId,
    success_url: successUrl,
    cancel_url: cancelUrl
  };
  
  // Add coupon code if provided
  if (couponCode) {
    payload.coupon_code = couponCode;
  }
  
  return api.post('/subscriptions/checkout', payload);
};

export const cancelSubscription = (immediately = false) => {
  return api.post('/subscriptions/cancel', null, {
    params: { immediately }
  });
};

export const fetchCouponStatus = (couponCode) => {
  return api.get(`/coupon/status/${couponCode}`);
};

// Daily Challenge API
export const fetchDailyChallenge = () => {
  return api.get('/daily-challenge/random-difficult');
};

// Tutor Dashboard API
export const fetchDifficultyDistribution = () => {
  return api.get('/api/tutor/dashboard/difficulty-distribution');
};

export default api; 